import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import logo from '../assets/logo.png';
import logo2 from '../assets/logo2.png';

const ORANGE = "#FF6600";
const LIGHT_ORANGE_BG = "rgba(255,102,0,0.07)";

const navStructure = [
  { label: 'Home', path: '/' },
  {
    label: 'About Us',
    dropdown: [
      { label: 'Our Team', path: '/about/OurTeam' },
      { label: 'Our Story', path: '/about/our-story' },
    ],
  },
  {
    label: 'Join Us',
    dropdown: [
      { label: 'Student Chapter', path: '/joinus/student-chapter' },
      { label: 'Become Partner', path: '/joinus/become-partner' },
    ],
  },
  {
    label: 'Programs',
    dropdown: [
      { label: 'Mentorship', path: '/programs/mentorship' },
      { label: 'Cybersecurity Basics', path: '/programs/cybersecurity-basics' },
      { label: 'Ethical Hacking', path: '/programs/ethical-hacking' },
      { label: 'Certification', path: '/programs/certification' },
      { label: 'Networking Summit', path: '/programs/networking-summit' },
      { label: 'Awareness Workshop', path: '/programs/awareness-workshop' },
      { label: 'Women In Cyber Panel', path: '/programs/women-in-cyber-panel' },
    ],
  },
  {
    label: 'Events',
    path: '/events',
  },
  { label: 'Resources', path: '/resources' },
];

const DropdownArrow = () => (
  <svg width="10" height="6" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: 4 }}>
    <path d="M2 2L6 6L10 2" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Navbar = () => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      setIsMobile(window.innerWidth < 900);
    }
    // Set initial value
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <nav style={{
      width: '100%',
      background: '#fff',
      boxShadow: '0 3px 12px rgba(0,0,0,0.05)',
      padding: '1rem 2rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      position: 'sticky',
      top: 0,
      zIndex: 100,
    }}>
      {/* Logo */}
      <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
        <img
          src={logo}
          alt="ETWiC Logo"
          style={{
            width: 120,
            height: 'auto',
            objectFit: 'contain',
            display: 'block',
            transition: 'filter 0.2s, background 0.2s',
            background: theme === 'dark' ? '#fff' : 'transparent',
            borderRadius: theme === 'dark' ? 18 : 0,
            boxShadow: theme === 'dark' ? '0 4px 24px rgba(0,0,0,0.10)' : 'none',
            padding: theme === 'dark' ? 8 : 0,
          }}
        />
      </Link>

      {/* Hamburger for mobile */}
      {isMobile && (
        <button
          style={{
            marginLeft: 'auto',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            fontSize: 28,
          }}
          aria-label="Open menu"
          onClick={() => setMobileMenuOpen(m => !m)}
        >
          <span style={{ fontSize: 32, color: ORANGE }}>{mobileMenuOpen ? '✕' : '☰'}</span>
        </button>
      )}

      {/* Desktop Nav */}
      {!isMobile && (
        <>
          <div style={{ flex: 1 }} />
          <ul
            style={{
              display: 'flex',
              alignItems: 'center',
              listStyle: 'none',
              gap: '1.4rem',
              margin: 0,
              padding: 0,
            }}
          >
            {navStructure.map((item, idx) => (
              <li
                key={item.label}
                style={{ position: 'relative' }}
                onMouseEnter={() => setOpenDropdown(idx)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <div style={{
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  color: 'var(--color-navbar-text)',
                  padding: '0.4rem 0.5rem',
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: openDropdown === idx ? LIGHT_ORANGE_BG : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease',
                }}>
                  {item.dropdown ? (
                    <>
                      {item.label}
                      {item.label !== "Events" && <DropdownArrow />}
                    </>
                  ) : (
                    <Link to={item.path} style={{ color: 'var(--color-navbar-text)', textDecoration: 'none' }}>
                      {item.label}
                    </Link>
                  )}
                </div>
                {/* Dropdown */}
                {item.dropdown && openDropdown === idx && (
                  <ul style={{
                    position: 'absolute',
                    top: '110%',
                    left: 0,
                    background: '#fff',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                    borderRadius: 8,
                    padding: '0.4rem 0',
                    margin: 0,
                    listStyle: 'none',
                    minWidth: 170,
                    zIndex: 100,
                  }}>
                    {item.dropdown.map(sub => (
                      <li key={sub.label} style={{
                        padding: '0.45rem 1rem',
                        fontSize: '0.9rem',
                        color: '#333',
                        transition: '0.2s',
                        borderRadius: 6,
                      }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = LIGHT_ORANGE_BG;
                          e.currentTarget.style.color = ORANGE;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#333';
                        }}
                      >
                        <Link to={sub.path} style={{ textDecoration: 'none', color: 'inherit' }}>
                          {sub.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            {/* Contact Us */}
            <li>
              <Link to="/contact" style={{
                fontSize: '0.95rem',
                fontWeight: 500,
                color: ORANGE,
                background: '#fff',
                padding: '0.5rem 1rem',
                borderRadius: '999px',
                border: `2px solid ${ORANGE}`,
                boxShadow: `0 0 0 4px rgba(255,102,0,0.07), 0 6px 18px rgba(255,102,0,0.2)`,
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                display: 'inline-block',
              }}>
                Contact Us
              </Link>
            </li>
          </ul>
        </>
      )}

      {/* Mobile Nav */}
      {isMobile && mobileMenuOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(255,255,255,0.97)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: 90,
            overflowY: 'auto',
          }}
        >
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, width: '100%', textAlign: 'center' }}>
            {navStructure.map((item, idx) => (
              <React.Fragment key={item.label}>
                <li style={{ marginBottom: 18 }}>
                  {item.dropdown ? (
                    <>
                      <div style={{ fontWeight: 800, fontSize: 22, color: ORANGE, marginBottom: 8, letterSpacing: '-0.5px' }}>{item.label}</div>
                      <ul style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        background: 'rgba(255,102,0,0.06)',
                        borderLeft: `4px solid ${ORANGE}`,
                        borderRadius: 10,
                        marginLeft: 24,
                        marginRight: 24,
                        marginBottom: 8,
                        boxShadow: '0 2px 8px rgba(255,102,0,0.07)',
                        transition: 'all 0.3s',
                      }}>
                        {item.dropdown.map(sub => (
                          <li key={sub.label} style={{
                            margin: '10px 0',
                            padding: '10px 0 10px 18px',
                            fontSize: 17,
                            fontWeight: 500,
                            color: '#333',
                            borderBottom: '1px solid #ffe0cc',
                            textAlign: 'left',
                            transition: 'background 0.2s',
                          }}>
                            <Link to={sub.path} style={{ color: '#333', textDecoration: 'none', display: 'block' }} onClick={() => setMobileMenuOpen(false)}>
                              {sub.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <Link to={item.path} style={{ color: '#333', fontSize: 20, textDecoration: 'none', fontWeight: 700, padding: '10px 0', display: 'block' }} onClick={() => setMobileMenuOpen(false)}>
                      {item.label}
                    </Link>
                  )}
                </li>
                {/* Divider between main nav sections except last */}
                {idx < navStructure.length - 1 && (
                  <li style={{ width: '70%', margin: '0 auto', borderBottom: '1.5px solid #ffe0cc', marginBottom: 12 }} />
                )}
              </React.Fragment>
            ))}
            <li style={{ marginTop: 24 }}>
              <Link to="/contact" style={{ color: ORANGE, fontWeight: 700, fontSize: 19, textDecoration: 'none', border: `2px solid ${ORANGE}`, borderRadius: 20, padding: '10px 28px', background: '#fff', display: 'inline-block', boxShadow: '0 2px 8px rgba(255,102,0,0.07)' }} onClick={() => setMobileMenuOpen(false)}>
                Contact Us
              </Link>
            </li>
          </ul>
        </div>
      )}

      {/* Theme Toggle Button */}
      <button
        onClick={toggleTheme}
        aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        style={{
          marginLeft: '1.5rem',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          outline: 'none',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {theme === 'dark' ? (
          // Sun icon for light mode
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FFA500" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
        ) : (
          // Moon icon for dark mode
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#181C26" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1111.21 3a7 7 0 109.79 9.79z"/></svg>
        )}
      </button>
    </nav>
  );
};

export default Navbar;
